import startCase from 'lodash/startCase';
import isEmpty from 'lodash/isEmpty';
import { SFTPConnectionStatus } from './schema/he/types/SFTPConnectionStatus/type';
import { escapeCsvFieldData, get } from './utils';

export const formatEthnicity = (ethnicity) => {
  if (isEmpty(ethnicity)) {
    return 'Unknown';
  }
  const ethnicitys = {
    aa: 'American Indian/Alaska Native',
    ai: 'American Indian/Alaska Native',
    am: 'American Indian/Alaska Native',
    americanindian: 'American Indian/Alaska Native',
    as: 'Asian',
    asian: 'Asian',
    ba: 'Black/African American',
    black: 'Black/African American',
    fi: 'Filipino',
    filipino: 'Filipino',
    hi: 'Hispanic/Latino',
    hispanic: 'Hispanic/Latino',
    mr: 'Multi-Racial',
    mu: 'Multi-Racial',
    multiracial: 'Multi-Racial',
    o: 'Other',
    ot: 'Other',
    other: 'Other',
    pa: 'Pacific Islander/Native Hawaiian',
    pi: 'Pacific Islander/Native Hawaiian',
    pn: 'Pacific Islander/Native Hawaiian',
    pacificislander: 'Pacific Islander/Native Hawaiian',
    u: 'Unknown',
    uk: 'Unknown',
    unknown: 'Unknown',
    unreportedbyschool: 'Unknown',
    wh: 'White',
    white: 'White',
  };

  const formattedEthnicity = ethnicitys[ethnicity.toLowerCase().replace(/[^a-z]/, '')];
  return formattedEthnicity || startCase(ethnicity.trim().toLowerCase().replace(/_/, ' '));
};

export const formatState = (state) => {
  if (!state) {
    return '';
  } else if (state.trim().length === 2) {
    return state.trim().toUpperCase();
  } else {
    return startCase(state.trim().toLowerCase());
  }
};

export const formatPhoneNumber = (s) => {
  const s2 = (s || '').replace(/\D/g, '');
  const m = s2.match(/^(\d{3})(\d{3})(\d{4})$/);
  return !m ? null : `(${m[1]}) ${m[2]}-${m[3]}`;
};

export const formatGender = (gender) => {
  if (isEmpty(gender)) {
    return 'Unknown';
  }
  const genders = {
    m: 'Male',
    male: 'Male',
    f: 'Female',
    female: 'Female',
    u: 'Unknown',
    uk: 'Unknown',
    unknow: 'Unknown',
    x: 'Non-Binary',
    nb: 'Non-Binary',
    nonbinary: 'Non-Binary',
  };
  const formattedGender = genders[gender.toLowerCase().replace(/[^a-z]/, '')];
  return formattedGender || startCase(gender.trim().toLowerCase());
};

export const CSV_COLUMNS = [
  'First Name',
  'Last Name',
  'Email',
  'Phone',
  'Address',
  'City',
  'State',
  'Zip',
  'Ethnicity',
  'Gender',
  'BirthDate',
  'SchoolName',
  'Ceeb',
  'Graduation Year',
  'Gpa',
  'Created/Modified Date',
  'Connection Status',
];

export const CSV_OPTIONAL_COLUMNS = ['Major 1', 'Major 2', 'Major 3', 'Source', 'Credits Earned', 'Desired Start Date'];

const toCSVData = (connections) =>
  connections.map((student) => {
    const csvObj = {};
    const disconnect = get(student, 'disconnectedStatus') === 1;
    const dateToUse = get(student, 'dateUpdated', '') || get(student, 'dateSubmitted', '');
    csvObj[CSV_COLUMNS[0]] = startCase(get(student, 'firstName', '').toLowerCase());
    csvObj[CSV_COLUMNS[1]] = startCase(get(student, 'lastName', '').toLowerCase());
    csvObj[CSV_COLUMNS[2]] = get(student, 'email', '').toLowerCase();
    csvObj[CSV_COLUMNS[3]] = formatPhoneNumber(get(student, 'phone', ''));
    csvObj[CSV_COLUMNS[4]] = startCase(get(student, 'address', '').toLowerCase());
    csvObj[CSV_COLUMNS[5]] = startCase(get(student, 'city', '').toLowerCase());
    csvObj[CSV_COLUMNS[6]] = formatState(get(student, 'state', ''));
    csvObj[CSV_COLUMNS[7]] = get(student, 'zip', '');
    csvObj[CSV_COLUMNS[8]] = formatEthnicity(get(student, 'ethnicity', ''));
    csvObj[CSV_COLUMNS[9]] = formatGender(get(student, 'gender', ''));
    csvObj[CSV_COLUMNS[10]] = get(student, 'birthDate', '');
    csvObj[CSV_COLUMNS[11]] = get(student, 'schoolName', '');
    csvObj[CSV_COLUMNS[12]] = get(student, 'ceeb', '');
    csvObj[CSV_COLUMNS[13]] = get(student, 'graduationYear', '');
    csvObj[CSV_COLUMNS[14]] = get(student, 'gpa', '');
    csvObj[CSV_COLUMNS[15]] = dateToUse || '';
    csvObj[CSV_COLUMNS[16]] = disconnect ? 'Disconnected' : 'Connected';

    // Connection (AMng) and Camino
    csvObj[CSV_OPTIONAL_COLUMNS[0]] = get(student, 'majors[0].name', '');
    csvObj[CSV_OPTIONAL_COLUMNS[1]] = get(student, 'majors[1].name', '');
    csvObj[CSV_OPTIONAL_COLUMNS[2]] = get(student, 'majors[2].name', '');

    // Camino only
    csvObj[CSV_OPTIONAL_COLUMNS[3]] = get(student, 'source', 'naviance');
    csvObj[CSV_OPTIONAL_COLUMNS[4]] = get(student, 'creditsEarned', '');
    csvObj[CSV_OPTIONAL_COLUMNS[5]] = get(student, 'transferBy', '');

    return csvObj;
  });

const getCsvColumns = (options) => {
  if (isEmpty(options)) return CSV_COLUMNS;

  const extraColumns = [];
  if (Array.isArray(options)) {
    // support an array of column names (legacy), but validate against allowed columns
    extraColumns.push(...options.filter((column) => CSV_OPTIONAL_COLUMNS.includes(column)));
  } else if (options.camino) {
    extraColumns.push('Major 1', 'Major 2', 'Major 3', 'Source', 'Credits Earned', 'Desired Start Date');
  } else if (options.connection) {
    extraColumns.push('Major 1', 'Major 2', 'Major 3');
  }
  return [...CSV_COLUMNS, ...extraColumns];
};

export const generateCsvForDownload = (connections, options = null) => {
  const translatedConnections = toCSVData(connections);

  const csvColumns = getCsvColumns(options);

  const headers = csvColumns.map((column) => `${escapeCsvFieldData(column)}`).join(',');
  const stringData = translatedConnections
    .map((item) => csvColumns.map((column) => `${escapeCsvFieldData(item[column])}`).join(','))
    .join('\r\n');
  return `${headers}\r\n${stringData}`;
};

/**
 * Takes an SFTPConnectionStatus that is returned from testing an SFTP connection and translates it to
 * a message suitable to show on the UI.
 */
export const translateSFTPConnectionErrorStatusToUserMessage = (status) => {
  switch (status) {
    case SFTPConnectionStatus.AUTH_FAILURE:
      return 'Authentication failed. Check that the host and connection credentials are correct. If you are using an SSH Public Key, make sure it is installed on your SFTP server.';
    case SFTPConnectionStatus.WRONG_PORT_FAILURE:
      return 'Port was refused. Check that the port number is correct.';
    case SFTPConnectionStatus.HOST_NOT_REACHABLE_FAILURE:
      return "Host can't be reached. Check that the host is correct, your SFTP server is accepting connections, or add the Hobsons Public IPs to your IP Whitelist.";
    case SFTPConnectionStatus.FINGERPRINT_CHANGED:
      return 'Server fingerprint mismatch detected. Please confirm detected fingerprint in Intersect [https://he.intersect.hobsons.com/]  Account Settings>SFTP Data Transfer and click the “Edit” link for your configuration.';
    default:
      // everything else is the FAILURE case
      return 'Connection or transfer failed. Possible reasons include a network connection interruption, insufficient SFTP server path permissions, no space on the SFTP server, attempting to transfer to a forbidden or non-existent directory, or some other scenario.';
  }
};
